import React, { useEffect, useState } from 'react'
import { Card, Collapse, Form, InputNumber, Space } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import MatchingForm from '../matching-form/MatchingForm'
import PatternIcon from '../../../../../../../../components/common/PatternIcon'
import { DroppableInput } from './DroppableInput'
import Title from 'antd/es/typography/Title'

const { Panel } = Collapse

const Steps = {
    MATCH: 0,
    REVIEW: 1,
}

const CategoriesForm = ({ originalCategories, rubricCategories, reviewedCategories }) => {
    // Determine if original and rubric categories exist
    const hasOriginalCategories = originalCategories?.length > 0
    const hasRubricCategories = rubricCategories?.length > 0
    const hasReviewedCategories = reviewedCategories?.length > 0

    // Function to initialize matched categories
    const getInitialMatchedCategories = () => {
        if (hasReviewedCategories) {
            return reviewedCategories.map(category => ({
                ...category,
                id: String(category.id),
            }))
        }
        if (hasRubricCategories) {
            return rubricCategories.map(category => ({
                ...category,
                id: String(category.id),
            }))
        } else if (hasOriginalCategories) {
            return originalCategories.map(category => ({
                ...category,
                id: String(category.id),
            }))
        } else {
            return []
        }
    }

    const initialStep =
        (hasOriginalCategories && !hasRubricCategories) || hasReviewedCategories ? Steps.REVIEW : Steps.MATCH

    const [currentStep, setCurrentStep] = useState(initialStep)
    const [matchedCategories, setMatchedCategories] = useState(getInitialMatchedCategories())

    useEffect(() => {
        setMatchedCategories(getInitialMatchedCategories())

        if ((hasOriginalCategories && !hasRubricCategories) || hasReviewedCategories) {
            setCurrentStep(Steps.REVIEW)
        } else {
            setCurrentStep(Steps.MATCH)
        }
    }, [originalCategories, rubricCategories])

    const handleMatchSubmit = ({ matchedItems }) => {
        const updatedItems = originalCategories.map(category => {
            const matchedCategory = matchedItems.find(item => item.matchingCategoryId == category.id)
            return {
                id: category.id,
                name: matchedCategory?.name || category.name,
                feedback: matchedCategory?.feedback || category.feedback,
                grade: matchedCategory?.grade || category.grade,
                matchingRubricCategoryId: matchedCategory?.id || null,
            }
        })
        setMatchedCategories(updatedItems)
        setCurrentStep(Steps.REVIEW)
    }

    const handleRevertMatches = () => {
        setMatchedCategories(getInitialMatchedCategories())
    }

    let content
    if (currentStep === Steps.MATCH) {
        content = (
            <MatchingForm
                originalItems={originalCategories}
                matchedItems={matchedCategories}
                renderItem={category => category.name}
                matchingProperty='matchingCategoryId'
                onSubmit={handleMatchSubmit}
                onRevert={handleRevertMatches}
                titleLeft='Original Categories'
                titleRight='Rubric Categories'
                titleLeftStyle={{ fontSize: '1rem', margin: '0 0 8px 0' }}
                titleRightStyle={{ fontSize: '1rem', margin: '0 0 8px 0' }}
                submitButtonText='Approve matches'
            />
        )
    }

    if (currentStep === Steps.REVIEW) {
        content = (
            <Space direction='vertical'>
                <Title level={4} style={{ margin: 0, fontSize: '1rem' }}>
                    Categories
                </Title>
                <Collapse accordion ghost bordered={false} className='mb-16'>
                    {matchedCategories.map((category, index) => {
                        const panelHeader = (
                            <Space size={8}>
                                <PatternIcon id={category.id} colorId={category.colorId} showId={false} />
                                {category.name}
                            </Space>
                        )

                        return (
                            <Panel header={panelHeader} key={index} forceRender>
                                <Form.Item name={`category-${index}-name`} label='Name' initialValue={category.name}>
                                    <DroppableInput
                                        droppableId={`form-category-${index}-name`}
                                        placeholder='Enter name'
                                        autoSize
                                        inputComponent={TextArea}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name={`category-${index}-feedback`}
                                    label='Feedback'
                                    initialValue={category.feedback}
                                >
                                    <DroppableInput
                                        droppableId={`form-category-${index}-feedback`}
                                        placeholder='Enter feedback'
                                        autoSize
                                        inputComponent={TextArea}
                                    />
                                </Form.Item>
                                <Form.Item name={`category-${index}-grade`} label='Grade' initialValue={category.grade}>
                                    <DroppableInput
                                        droppableId={`form-category-${index}-grade`}
                                        autoSize
                                        inputComponent={InputNumber}
                                    />
                                </Form.Item>
                            </Panel>
                        )
                    })}
                </Collapse>
            </Space>
        )
    }

    return (
        <Card className='mb-16' size='small'>
            {content}
        </Card>
    )
}

export default CategoriesForm
